/**
 * Formatting shared by every scorekeeping surface.
 *
 * These were written four times over, once per screen, which is fine until two
 * copies disagree. Scores and positions appear side by side on the club screen,
 * the machine page and the leaderboard, and a player reading "4th" in one place
 * and "4" in another notices even if they cannot say what is wrong.
 */

/** Scores are long and read at a glance, so they are always grouped. */
export const formatScore = (value) => Number(value || 0).toLocaleString('en-US');

const ORDINALS = ['th', 'st', 'nd', 'rd'];

/** 1 becomes 1st, 2 becomes 2nd, 11 through 13 stay 11th through 13th. */
export const ordinal = (n) => {
  const v = Number(n) % 100;
  return `${n}${ORDINALS[(v - 20) % 10] || ORDINALS[v] || ORDINALS[0]}`;
};

/**
 * Whole days left in the month, in the viewer's own time.
 *
 * Only ever decoration. A competition month is settled in club-local time by the
 * server, and nothing here decides when a month ends.
 */
export const daysLeftInMonth = () => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return Math.max(0, Math.ceil((end - now) / 86400000));
};

/** 24-hour wall clock, for "as of" lines on the club screen. */
export const clockTime = (date) => new Date(date).toLocaleTimeString('en-US', {
  hour: '2-digit', minute: '2-digit', hour12: false
});

/**
 * A big number read back in words, which is how people check zeros.
 * "1,284,660" becomes "1.28 million". Returns null below a thousand, where
 * grouping already makes it obvious.
 */
export const inWords = (value) => {
  const n = Number(value);
  if (!n) return null;
  if (n >= 1e9) return `${(n / 1e9).toFixed(2).replace(/\.?0+$/, '')} billion`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2).replace(/\.?0+$/, '')} million`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1).replace(/\.?0+$/, '')} thousand`;
  return null;
};
